import { KeyMap } from './key-map';

export type TransitionData =
  | TransitionShiftData
  | TransitionUpdateData
  | TransitionUpshiftData
  | TransitionResourceShiftData;

/**
 * activityShift
 *  - UPDATE: operations are considered activities
 * currentActivities
 *  - UPDATE & UPSHIFT: operations are considered activities
 */
export interface Transition {
  activityShift: number; // number of activities shifted to low-carbon
  currentActivities: number; // activities still in LHS (based on operations bau)
  emissionBau: number; // sum of CCC emissionBau * cohort
  requestedWeight: number;
  activityToCarbonAbatementFactor: number | undefined;
  newWeight?: number;
}

export interface ParameterTransition extends Transition {
  currentParameterValue?: number;
  parameterPotential?: number;
  unaffectedParameterValue?: number;
  capParameterValue?: number;
  transitionDirection?: TransitionUpdateDirection;
  weightedEmissionFactor: number;
}

export interface TransitionShiftData extends Transition {
  activitiesBau: number; // activities in LHS BAU
  alternativeActivitiesBau: number; // activities in RHS BAU
  alternativeEmissionBau: number; // sum of RHS CCC emissionBau * cohort
  maximumNetLhsCccSavings: number; // net carbon abatement (net(ccc), gross(parameters))
  carbonAbatement: {
    net: number; // net emissions saved
    gross?: number; // gross emissions saved from lhs, SHIFT: using emission bau parameters
  };
  emission: number; // sum of CCC emission * cohort
  transitionedEmissions: number; // New emissions from RHS, always >= 0
  potential?: ConstrainedAndUnConstrainedPotential;
  sinks?: number; // TODO: when model supports it, set sinks (if transitionedEmissions are actually < 0, it ends up here instead.)
  additiveOptions?: {
    relativeWeight: number;
  };
  operationsShifted: KeyMap<number>; // number of operations shifted, per CCC
}

export interface TransitionUpdateData extends ParameterTransition {
  carbonAbatement: {
    gross: number; // gross emissions saved from lhs, UPDATE: using emission improved parameters
  };
  potential: ConstrainedAndUnConstrainedPotential;
  operationsFullyAffected: KeyMap<number>; // number of operations fully affected i.e. if we consider the effect of the Update as affecting some operations to the max potential and the rest not at all, this shows the number of fully affected operations, per CCC
  referenceEnergyIntensity: number;
  referenceEnergyIntensityPerCcc: KeyMap<KeyMap<number>>; // ccc and work
  maxUpdatedEnergyIntensity: number;
}

export interface TransitionUpshiftData extends ParameterTransition {
  carbonAbatement: {
    gross: number; // gross emissions saved from lhs, UPSHIFT: using emission bau parameters
  };
  emissionsInReachBau: number; // sum of CCC emissionBau that could potentially be removed if target is maxed
  operationsUpshifted: KeyMap<number>; // number of operations upshifted, per CCC
  operationsInScope: KeyMap<number>; // number of operations in scope( total operations * cohort), per CCC
  potential?: ConstrainedAndUnConstrainedPotential;
  weightedEnergyIntensity: number;
}

export interface TransitionResourceShiftData extends Transition {
  proportionCap: number;
  proportionStart: number;
  carbonAbatement: {
    gross: number; // gross emissions saved from lhs
  };
  transitionedEmissions: number;
  operationsAffected: KeyMap<number>;
  potential?: ConstrainedAndUnConstrainedPotential;
}

interface ConstrainedAndUnConstrainedPotential {
  constrained: number; // maximal potential (based on operations left), reference_scenario
  unconstrained: number; // maximal potential (based on operations left), bau
}

export enum TransitionUpdateDirection {
  Up = 'up',
  Down = 'down',
}

export type SupplyTransitionData = SupplyAlterationData | SupplyUpdateData;

export interface SupplyAlterationData {
  operations: number;
  totalAlteration: number;
  emissionsFromCcc: number;
}

export interface SupplyUpdateData {
  value: number;
  originalValue: number;
  parameterUnit: string;
}

export interface TransitionElementCosts {
  cccId: string;
  costs: CostResults[];
}

export interface CostResults {
  name: string;
  cost: number;
  discountedCost: number;
}
